import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { linkify } from "@/components/ExpandableText";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, MessageSquare, Users, Image as ImageIcon, Play, Send, Trash2,
  ImagePlus, Video as VideoIcon, X, MoreVertical, Pencil, EyeOff, Eye,
  Repeat2, Globe, Lock, MessageCircle, Trophy, Clapperboard,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import FollowButton from "@/components/FollowButton";
import UserActionsMenu from "@/components/UserActionsMenu";
import PostReactions from "@/components/PostReactions";
import PostCarousel from "@/components/PostCarousel";
import ProfileReelsTab from "@/components/ProfileReelsTab";
import RepostOriginal from "@/components/RepostOriginal";
import LinkPreviewCard from "@/components/LinkPreviewCard";
import CommentThread from "@/components/CommentThread";
import AchievementsPanel from "@/components/AchievementsPanel";
import { useAuth } from "@/contexts/AuthContext";
import { compressImageIfNeeded } from "@/lib/imageCompress";
import { useNotificationStream } from "@/hooks/useNotificationStream";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UserProfile() {
  const { userId } = useParams();
  const { user: me } = useAuth();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [stats, setStats] = useState({ followers_count: 0, following_count: 0 });
  const [relationship, setRelationship] = useState(null);
  const [album, setAlbum] = useState({ photos: [], videos: [], total: 0 });
  const [lightbox, setLightbox] = useState(null);
  const [draft, setDraft] = useState({ content: "", image_url: "", video_url: "", visibility: "public" });
  const [publishing, setPublishing] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Edit / Repost dialog state (dialogs are mounted permanently — React 18 portal rule)
  const [editing, setEditing] = useState(null);          // { post_id, content, visibility }
  const [editOpen, setEditOpen] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [reposting, setReposting] = useState(null);      // full source post
  const [repostOpen, setRepostOpen] = useState(false);
  const [repostComment, setRepostComment] = useState("");
  const [sharingRepost, setSharingRepost] = useState(false);
  // Comments open/closed per post (network I/O lives inside <CommentThread />)
  const [openComments, setOpenComments] = useState({});
  const isMe = me && userId && me.user_id === userId;

  // Live comment updates via SSE — subscribe only to posts with the thread open
  const openedIds = React.useMemo(
    () => Object.keys(openComments).filter((k) => openComments[k]),
    [openComments]
  );
  const { lastCommentEvent } = useNotificationStream({ postIds: openedIds });
  useEffect(() => {
    if (!lastCommentEvent) return;
    const { type, post_id, payload } = lastCommentEvent;
    setPosts((prev) => prev.map((p) => {
      if (p.post_id !== post_id) return p;
      const list = p.comments || [];
      if (type === "new_comment") {
        if (list.some((c) => c.comment_id === payload.comment_id)) return p;
        return { ...p, comments: [...list, payload] };
      }
      if (type === "comment_edited") {
        return { ...p, comments: list.map((c) => c.comment_id === payload.comment_id ? payload : c) };
      }
      if (type === "comment_deleted") {
        const removed = new Set(payload.removed_ids || [payload.comment_id]);
        return { ...p, comments: list.filter((c) => !removed.has(c.comment_id)) };
      }
      return p;
    }));
  }, [lastCommentEvent]);

  const loadAll = async () => {
    if (!userId) return;
    // Promise.allSettled so one failed request never leaves the page stuck on "Carregando..."
    const [u, p, st, fol, fing, med, rel] = await Promise.allSettled([
      axios.get(`${API}/users/${userId}`),
      axios.get(`${API}/users/${userId}/posts`),
      axios.get(`${API}/users/${userId}/follow-status`),
      axios.get(`${API}/users/${userId}/followers`),
      axios.get(`${API}/users/${userId}/following`),
      axios.get(`${API}/users/${userId}/media?limit=120`),
      axios.get(`${API}/users/${userId}/relationship`),
    ]);
    if (u.status === "fulfilled") setProfile(u.value.data);
    else { toast.error("Não foi possível carregar o perfil"); return; }
    if (p.status === "fulfilled") setPosts(p.value.data);
    if (st.status === "fulfilled") setStats({ followers_count: st.value.data.followers_count, following_count: st.value.data.following_count });
    if (fol.status === "fulfilled") setFollowers(fol.value.data);
    if (fing.status === "fulfilled") setFollowing(fing.value.data);
    if (med.status === "fulfilled") setAlbum(med.value.data);
    if (rel.status === "fulfilled") setRelationship(rel.value.data);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAll(); }, [userId]);

  const uploadMedia = async (file, kind) => {
    if (!file) return;
    setUploading(true);
    try {
      // Compress images client-side to avoid 502/timeout on large phone photos
      if (kind !== "video") file = await compressImageIfNeeded(file);
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      const raw = r.data?.url || r.data?.file_url || r.data?.path;
      // Store the relative path (/api/files/...). Same-origin resolution keeps
      // media URLs valid across preview and production domains.
      const url = raw?.startsWith("http") ? raw : raw;
      setDraft((d) => ({ ...d, [kind === "video" ? "video_url" : "image_url"]: url }));
      toast.success(kind === "video" ? "Vídeo enviado" : "Imagem enviada");
    } catch (err) {
      const msg = err.response?.data?.detail
        || (err.code === "ECONNABORTED" ? "Tempo esgotado — tente um arquivo menor" : null)
        || "Erro no upload";
      toast.error(msg);
    }
    setUploading(false);
  };

  const publish = async () => {
    if (!draft.content.trim() && !draft.image_url && !draft.video_url) {
      toast.error("Escreva algo ou envie uma mídia");
      return;
    }
    setPublishing(true);
    try {
      await axios.post(`${API}/posts`, {
        content: draft.content || (draft.video_url ? "🎥 Novo vídeo" : ""),
        image_url: draft.image_url || null,
        video_url: draft.video_url || null,
        visibility: draft.visibility,
      });
      toast.success(draft.visibility === "private" ? "Publicado só para seguidores 🔒" : "Publicado!");
      setDraft({ content: "", image_url: "", video_url: "", visibility: "public" });
      loadAll();
    } catch { toast.error("Erro ao publicar"); }
    setPublishing(false);
  };

  const deletePost = async (post_id) => {
    if (!window.confirm("Excluir esta publicação? Não pode ser desfeito.")) return;
    try {
      await axios.delete(`${API}/posts/${post_id}`);
      toast.success("Publicação excluída");
      loadAll();
    } catch { toast.error("Erro ao excluir"); }
  };

  const openEdit = (post) => {
    setEditing({ post_id: post.post_id, content: post.content || "", visibility: post.visibility || "public" });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSavingEdit(true);
    try {
      await axios.patch(`${API}/posts/${editing.post_id}`, {
        content: editing.content,
        visibility: editing.visibility,
      });
      toast.success("Publicação atualizada");
      setEditOpen(false);
      loadAll();
    } catch { toast.error("Erro ao editar"); }
    setSavingEdit(false);
  };

  const toggleHide = async (post) => {
    const next = post.visibility === "hidden" ? "public" : "hidden";
    try {
      await axios.patch(`${API}/posts/${post.post_id}`, { visibility: next });
      toast.success(next === "hidden" ? "Publicação ocultada" : "Publicação reexibida");
      loadAll();
    } catch { toast.error("Erro ao alterar visibilidade"); }
  };

  const openRepost = (post) => {
    setReposting(post);
    setRepostComment("");
    setRepostOpen(true);
  };

  const submitRepost = async () => {
    if (!reposting) return;
    setSharingRepost(true);
    try {
      const r = await axios.post(`${API}/posts/${reposting.post_id}/repost`, { comment: repostComment });
      if (r.data?.already) toast.info("Você já compartilhou este post");
      else toast.success("Compartilhado no feed para todos!");
      setRepostOpen(false);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao compartilhar");
    }
    setSharingRepost(false);
  };

  const toggleComments = (postId) => {
    setOpenComments((prev) => ({ ...prev, [postId]: !prev[postId] }));
  };

  const refreshStats = async () => {
    try {
      const [st, fol] = await Promise.all([
        axios.get(`${API}/users/${userId}/follow-status`),
        axios.get(`${API}/users/${userId}/followers`),
      ]);
      setStats({ followers_count: st.data.followers_count, following_count: st.data.following_count });
      setFollowers(fol.data);
    } catch {}
  };

  if (!profile) return <div className="max-w-4xl mx-auto p-8">Carregando...</div>;

  return (
    <div className="max-w-4xl mx-auto space-y-4" data-testid={`profile-page-${userId}`}>
      <Link to="/feed" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="w-4 h-4" /> Voltar
      </Link>

      <Card className="p-6 bg-gradient-to-br from-primary/10 to-purple-500/10 border-primary/30">
        <div className="flex items-start gap-4 flex-wrap">
          <button onClick={() => album.total > 0 && setLightbox(album.photos[0] || album.videos[0])}
            className={album.total > 0 ? "cursor-pointer hover:opacity-90 transition" : ""}
            data-testid="profile-avatar-btn" title={album.total > 0 ? "Ver álbum" : ""}>
            <Avatar className="w-24 h-24 border-4 border-background shadow">
              <AvatarImage src={profile.picture} />
              <AvatarFallback className="text-2xl">{profile.name?.[0]}</AvatarFallback>
            </Avatar>
          </button>
          <div className="flex-1 min-w-[240px]">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold" data-testid="profile-name">{profile.name}</h1>
              {profile.is_admin && <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary text-primary-foreground">ADMIN</span>}
              {profile.is_private && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-700 flex items-center gap-1" data-testid="profile-private-badge"><Lock className="w-3 h-3" /> Privado</span>}
            </div>
            <p className="text-sm text-muted-foreground mt-1">{profile.bio || <span className="italic opacity-60">Sem bio</span>}</p>
            <div className="flex gap-4 mt-3 text-sm">
              <span data-testid="profile-followers"><strong>{stats.followers_count}</strong> seguidores</span>
              <span data-testid="profile-following"><strong>{stats.following_count}</strong> seguindo</span>
              <span data-testid="profile-posts-count"><strong>{posts.length}</strong> posts</span>
            </div>
          </div>
          <div className="flex gap-2 items-center">
            {!relationship?.blocked_by_me && <FollowButton userId={userId} onChange={refreshStats} />}
            {me?.user_id !== userId && relationship?.can_message !== false && (
              <Link to={`/chat/${userId}`}>
                <Button size="sm" variant="outline" className="rounded-full gap-1" data-testid="profile-message-btn">
                  <MessageSquare className="w-3.5 h-3.5" /> Mensagem
                </Button>
              </Link>
            )}
            {me?.user_id !== userId && (
              <UserActionsMenu userId={userId} userName={profile.name} relationship={relationship} onChanged={loadAll} />
            )}
          </div>
        </div>
      </Card>

      {relationship && !isMe && (relationship.blocked_by_me || relationship.blocked_me || relationship.can_view_posts === false || (relationship.is_private && !relationship.following && !me?.is_admin)) ? (
        <Card className="p-10 text-center" data-testid="profile-private-gate">
          <div className="w-14 h-14 mx-auto rounded-full bg-muted flex items-center justify-center mb-3"><Lock className="w-6 h-6 text-muted-foreground" /></div>
          {relationship.blocked_by_me ? (
            <>
              <h3 className="font-semibold">Você bloqueou {profile.name}</h3>
              <p className="text-sm text-muted-foreground mt-1">Desbloqueie pelo menu ⋮ para voltar a ver as publicações.</p>
            </>
          ) : relationship.blocked_me ? (
            <>
              <h3 className="font-semibold">Conteúdo indisponível</h3>
              <p className="text-sm text-muted-foreground mt-1">Você não pode ver as publicações deste usuário.</p>
            </>
          ) : (
            <>
              <h3 className="font-semibold">Esta conta é privada</h3>
              <p className="text-sm text-muted-foreground mt-1">{relationship.requested ? "Sua solicitação está aguardando aprovação." : `Solicite para seguir ${profile.name} e ver suas publicações, álbum e seguidores.`}</p>
            </>
          )}
        </Card>
      ) : (
      <Tabs defaultValue={new URLSearchParams(window.location.search).get("tab") === "achievements" ? "achievements" : "posts"}>
        <div className="-mx-4 px-4 sm:mx-0 sm:px-0 overflow-x-auto scrollbar-hide" data-testid="profile-tabs-scroll">
          <TabsList className="rounded-full inline-flex w-max min-w-full sm:min-w-0 justify-start">
            <TabsTrigger value="posts" className="rounded-full shrink-0 whitespace-nowrap" data-testid="profile-tab-posts">Publicações ({posts.length})</TabsTrigger>
            <TabsTrigger value="album" className="rounded-full shrink-0 whitespace-nowrap" data-testid="profile-tab-album">Álbum ({album.total})</TabsTrigger>
            <TabsTrigger value="reels" className="rounded-full shrink-0 whitespace-nowrap gap-1" data-testid="profile-tab-reels"><Clapperboard className="w-3.5 h-3.5" /> Reels</TabsTrigger>
            <TabsTrigger value="followers" className="rounded-full shrink-0 whitespace-nowrap" data-testid="profile-tab-followers">Seguidores ({followers.length})</TabsTrigger>
            <TabsTrigger value="following" className="rounded-full shrink-0 whitespace-nowrap" data-testid="profile-tab-following">Seguindo ({following.length})</TabsTrigger>
            <TabsTrigger value="achievements" className="rounded-full gap-1 shrink-0 whitespace-nowrap" data-testid="profile-tab-achievements"><Trophy className="w-3.5 h-3.5" /> Conquistas</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="achievements" className="mt-4">
          <AchievementsPanel userId={userId} isMe={!!isMe} />
        </TabsContent>

        <TabsContent value="posts" className="mt-4 space-y-3">
          {isMe && (
            <Card className="p-4 space-y-3" data-testid="profile-composer">
              <div className="flex items-center gap-2">
                <Avatar className="w-8 h-8">
                  <AvatarImage src={profile.picture} />
                  <AvatarFallback>{profile.name?.[0]}</AvatarFallback>
                </Avatar>
                <span className="text-sm font-medium">Publicar no seu perfil</span>
              </div>
              <Textarea
                rows={3}
                value={draft.content}
                onChange={(e) => setDraft({ ...draft, content: e.target.value })}
                placeholder="No que você está pensando?"
                data-testid="profile-composer-input"
              />
              {draft.image_url && (
                <div className="relative">
                  <img src={draft.image_url} alt="preview" className="w-full max-h-56 object-cover rounded-lg" />
                  <button onClick={() => setDraft({ ...draft, image_url: "" })}
                    className="absolute top-1 right-1 bg-black/70 text-white p-1 rounded-full"
                    data-testid="profile-composer-clear-image">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
              {draft.video_url && (
                <div className="relative">
                  <video src={draft.video_url} controls className="w-full max-h-56 rounded-lg" />
                  <button onClick={() => setDraft({ ...draft, video_url: "" })}
                    className="absolute top-1 right-1 bg-black/70 text-white p-1 rounded-full"
                    data-testid="profile-composer-clear-video">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <label className="inline-flex items-center gap-1 text-sm cursor-pointer px-3 py-1.5 rounded-md border hover:bg-accent">
                    <ImagePlus className="w-4 h-4" /> Foto
                    <input type="file" accept="image/*" className="hidden"
                      onChange={(e) => uploadMedia(e.target.files?.[0], "image")}
                      data-testid="profile-composer-upload-image" disabled={uploading} />
                  </label>
                  <label className="inline-flex items-center gap-1 text-sm cursor-pointer px-3 py-1.5 rounded-md border hover:bg-accent">
                    <VideoIcon className="w-4 h-4" /> Vídeo
                    <input type="file" accept="video/*" className="hidden"
                      onChange={(e) => uploadMedia(e.target.files?.[0], "video")}
                      data-testid="profile-composer-upload-video" disabled={uploading} />
                  </label>
                  <label className="inline-flex items-center gap-1 text-xs cursor-pointer">
                    <input type="checkbox" checked={draft.visibility === "private"}
                      onChange={(e) => setDraft({ ...draft, visibility: e.target.checked ? "private" : "public" })}
                      data-testid="profile-composer-private" />
                    🔒 Só seguidores
                  </label>
                </div>
                <Button onClick={publish} disabled={publishing || uploading} data-testid="profile-composer-publish">
                  <Send className="w-4 h-4 mr-1" /> {publishing ? "Publicando..." : "Publicar"}
                </Button>
              </div>
            </Card>
          )}

          {posts.length === 0 && <Card className="p-6 text-center text-sm text-muted-foreground">Sem publicações visíveis.</Card>}
          {posts.map(p => (
            <PostItem
              key={p.post_id}
              post={p}
              isMe={isMe}
              canRepost={!!me}
              commentsOpen={!!openComments[p.post_id]}
              onToggleComments={() => toggleComments(p.post_id)}
              onNewComment={(newComment) => setPosts((prev) => prev.map((pp) =>
                pp.post_id === p.post_id
                  ? { ...pp, comments: [...(pp.comments || []), newComment] }
                  : pp
              ))}
              onCommentLocalUpdate={(updated) => setPosts((prev) => prev.map((pp) =>
                pp.post_id === p.post_id
                  ? {
                      ...pp,
                      comments: (pp.comments || []).map((c) =>
                        c.comment_id === updated.comment_id ? updated : c),
                    }
                  : pp
              ))}
              onCommentDeleted={(commentId, removed) => setPosts((prev) => prev.map((pp) =>
                pp.post_id === p.post_id
                  ? {
                      ...pp,
                      comments: (pp.comments || []).filter((c) =>
                        c.comment_id !== commentId && c.parent_comment_id !== commentId),
                    }
                  : pp
              ))}
              onCommentEdited={(updated) => setPosts((prev) => prev.map((pp) =>
                pp.post_id === p.post_id
                  ? {
                      ...pp,
                      comments: (pp.comments || []).map((c) =>
                        c.comment_id === updated.comment_id ? { ...c, ...updated } : c),
                    }
                  : pp
              ))}
              onEdit={() => openEdit(p)}
              onHide={() => toggleHide(p)}
              onDelete={() => deletePost(p.post_id)}
              onRepost={() => openRepost(p)}
            />
          ))}
        </TabsContent>

        <TabsContent value="reels" className="mt-4" data-testid="profile-reels">
          <ProfileReelsTab userId={userId} isMe={isMe} />
        </TabsContent>

        <TabsContent value="album" className="mt-4" data-testid="profile-album">
          {album.total === 0 ? (
            <Card className="p-6 text-center text-sm text-muted-foreground">Ainda não há fotos ou vídeos.</Card>
          ) : (
            <div className="space-y-6">
              {album.photos.length > 0 && (
                <div>
                  <h3 className="font-semibold flex items-center gap-2 mb-2"><ImageIcon className="w-4 h-4" /> Fotos ({album.photos.length})</h3>
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                    {album.photos.map((m) => (
                      <button key={m.post_id} onClick={() => setLightbox(m)}
                        className="aspect-square rounded-lg overflow-hidden bg-slate-100 hover:opacity-90 transition"
                        data-testid={`album-photo-${m.post_id}`}>
                        <img src={m.url} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {album.videos.length > 0 && (
                <div>
                  <h3 className="font-semibold flex items-center gap-2 mb-2"><Play className="w-4 h-4" /> Vídeos ({album.videos.length})</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                    {album.videos.map((m) => (
                      <button key={m.post_id} onClick={() => setLightbox(m)}
                        className="aspect-video rounded-lg overflow-hidden bg-slate-900 relative group hover:opacity-90 transition"
                        data-testid={`album-video-${m.post_id}`}>
                        <video src={m.url} muted className="w-full h-full object-cover" />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Play className="w-8 h-8 text-white drop-shadow-lg" fill="currentColor" />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="followers" className="mt-4">
          <UserList users={followers} emptyText="Ninguém segue esse usuário ainda." testidPrefix="follower"
            onRemove={isMe ? async (u) => {
              if (!window.confirm(`Remover ${u.name} dos seus seguidores?`)) return;
              try { await axios.delete(`${API}/users/${u.user_id}/follower`); setFollowers((p) => p.filter((x) => x.user_id !== u.user_id)); toast.success("Seguidor removido"); refreshStats(); }
              catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
            } : undefined} />
        </TabsContent>
        <TabsContent value="following" className="mt-4">
          <UserList users={following} emptyText="Esse usuário não segue ninguém." testidPrefix="following" />
        </TabsContent>
      </Tabs>
      )}

      {/* Lightbox for album (mounted permanently) */}
      <Dialog open={!!lightbox} onOpenChange={(v) => !v && setLightbox(null)}>
        <DialogContent className="max-w-3xl p-2" data-testid="album-lightbox">
          {lightbox && (
            <div className="space-y-2">
              {lightbox.type === "video"
                ? <video src={lightbox.url} controls autoPlay className="w-full max-h-[70vh] rounded-lg" />
                : <img src={lightbox.url} alt="" className="w-full max-h-[70vh] object-contain rounded-lg" />}
              {lightbox.content && <p className="text-sm px-2">{lightbox.content}</p>}
              <div className="flex items-center gap-4 text-xs text-muted-foreground px-2">
                <span>❤️ {lightbox.likes || 0}</span>
                <span>👁 {(lightbox.views || 0).toLocaleString("pt-BR")} visualizações</span>
                <Link to={`/feed#post-${lightbox.post_id}`} className="ml-auto underline hover:text-primary">Ver publicação</Link>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit dialog — mounted permanently to avoid React 18 portal unmount bug */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg" data-testid="edit-post-dialog">
          <DialogHeader>
            <DialogTitle>Editar publicação</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <Textarea
                rows={5}
                value={editing.content}
                onChange={(e) => setEditing({ ...editing, content: e.target.value })}
                placeholder="Atualize o conteúdo..."
                data-testid="edit-post-content"
              />
              <div>
                <label className="text-xs text-muted-foreground">Visibilidade</label>
                <Select
                  value={editing.visibility}
                  onValueChange={(v) => setEditing({ ...editing, visibility: v })}
                >
                  <SelectTrigger className="mt-1" data-testid="edit-post-visibility">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="public"><span className="inline-flex items-center gap-2"><Globe className="w-3.5 h-3.5" /> Público</span></SelectItem>
                    <SelectItem value="private"><span className="inline-flex items-center gap-2"><Lock className="w-3.5 h-3.5" /> Só seguidores</span></SelectItem>
                    <SelectItem value="hidden"><span className="inline-flex items-center gap-2"><EyeOff className="w-3.5 h-3.5" /> Oculto (só você)</span></SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} data-testid="edit-post-cancel">Cancelar</Button>
            <Button onClick={saveEdit} disabled={savingEdit} data-testid="edit-post-save">
              {savingEdit ? "Salvando..." : "Salvar alterações"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Repost dialog — mounted permanently */}
      <Dialog open={repostOpen} onOpenChange={setRepostOpen}>
        <DialogContent className="max-w-lg" data-testid="repost-dialog">
          <DialogHeader>
            <DialogTitle>Compartilhar novamente</DialogTitle>
          </DialogHeader>
          {reposting && (
            <div className="space-y-3">
              <Textarea
                rows={3}
                value={repostComment}
                onChange={(e) => setRepostComment(e.target.value)}
                placeholder="Adicione um comentário (opcional)..."
                data-testid="repost-comment"
              />
              <Card className="p-3 bg-muted/40">
                <div className="text-[11px] text-muted-foreground mb-1">Publicação original</div>
                <p className="text-sm whitespace-pre-wrap line-clamp-4">{linkify(reposting.content)}</p>
                {reposting.image_url && <img src={reposting.image_url} alt="" className="w-full max-h-40 object-cover rounded mt-2" />}
              </Card>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRepostOpen(false)} data-testid="repost-cancel">Cancelar</Button>
            <Button onClick={submitRepost} disabled={sharingRepost} data-testid="repost-submit">
              <Repeat2 className="w-4 h-4 mr-1" />
              {sharingRepost ? "Compartilhando..." : "Compartilhar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PostItem({
  post, isMe, canRepost,
  commentsOpen, onToggleComments, onNewComment, onCommentLocalUpdate, onCommentDeleted, onCommentEdited,
  onEdit, onHide, onDelete, onRepost,
}) {
  const p = post;
  const isRepost = !!p.is_repost;
  return (
    <Card className="p-4" data-testid={`profile-post-${p.post_id}`}>
      {isRepost && (
        <div className="flex items-center gap-1 text-[11px] text-muted-foreground mb-2">
          <Repeat2 className="w-3.5 h-3.5" />
          Compartilhado {p.original_user_name ? <>de <Link to={`/user/${p.original_user_id}`} className="font-medium hover:underline">{p.original_user_name}</Link></> : null}
        </div>
      )}
      <div className="flex items-center gap-2 mb-2">
        <div className="text-xs text-muted-foreground flex-1">
          {new Date(p.created_at).toLocaleString()}
          {p.edited_at && <span className="ml-2 italic opacity-70">(editado)</span>}
        </div>
        {p.visibility === "private" && <span className="text-[9px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-700 dark:text-amber-300">🔒 Privado</span>}
        {p.is_live_recording && <span className="text-[9px] px-2 py-0.5 rounded-full bg-red-500/15 text-red-600 dark:text-red-300 font-bold uppercase tracking-wide" data-testid={`post-live-replay-${p.post_id}`}>Live gravada</span>}
        {p.visibility === "hidden" && <span className="text-[9px] px-2 py-0.5 rounded-full bg-slate-500/20 text-slate-700 dark:text-slate-300">👁‍🗨 Oculto</span>}
        {isMe && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" data-testid={`profile-post-menu-${p.post_id}`}>
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              {!isRepost && (
                <DropdownMenuItem onClick={onEdit} data-testid={`profile-post-edit-${p.post_id}`}>
                  <Pencil className="w-4 h-4 mr-2" /> Editar
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={onHide} data-testid={`profile-post-hide-${p.post_id}`}>
                {p.visibility === "hidden"
                  ? <><Eye className="w-4 h-4 mr-2" /> Reexibir</>
                  : <><EyeOff className="w-4 h-4 mr-2" /> Ocultar</>}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={onDelete}
                className="text-red-500 focus:text-red-600"
                data-testid={`profile-post-delete-${p.post_id}`}
              >
                <Trash2 className="w-4 h-4 mr-2" /> Excluir
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {p.content && <p className="whitespace-pre-wrap text-sm break-words" data-testid={`profile-post-content-${p.post_id}`}>{linkify(p.content, { mentions: p.mentions })}</p>}

      {isRepost && <div className="mt-2"><RepostOriginal p={p} testidPrefix="up-repost" /></div>}
      {!isRepost && p.link_preview && <div className="mt-2"><LinkPreviewCard preview={p.link_preview} testid={`up-post-link-${p.post_id}`} /></div>}

      {!isRepost && (() => {
        const g = Array.isArray(p.image_urls) && p.image_urls.length ? p.image_urls : (p.image_url ? [p.image_url] : []);
        if (!g.length) return null;
        return (
          <div>
            {p.album_title && (
              <div className="flex items-center gap-2 mb-2" data-testid={`up-album-header-${p.post_id}`}>
                <span className="text-base">📚</span>
                <div>
                  <div className="text-sm font-semibold leading-tight">{p.album_title}</div>
                  <div className="text-[11px] text-muted-foreground">Álbum · {g.length} fotos</div>
                </div>
              </div>
            )}
            <PostCarousel urls={g} testidPrefix={`upcp-${p.post_id}`} />
          </div>
        );
      })()}
      {!isRepost && p.video_url && <video src={p.video_url} controls className="w-full max-h-80 rounded-lg mt-2" />}

      {/* Interactive footer: reactions + comment toggle + share */}
      <div className="mt-3 flex items-center justify-between text-sm text-muted-foreground pt-3 border-t border-border">
        <PostReactions
          postId={p.post_id}
          initialReactions={p.reactions}
          initialMy={p.my_reaction}
          initialTotal={p.reactions_total ?? (p.likes?.length || 0)}
        />
        <button
          onClick={onToggleComments}
          data-testid={`profile-post-comment-btn-${p.post_id}`}
          className="flex items-center gap-2 hover:text-primary transition-colors"
        >
          <MessageCircle className="w-5 h-5" /> {(p.comments || []).length}
        </button>
        {canRepost && p.visibility !== "hidden" ? (
          <button
            onClick={onRepost}
            data-testid={`profile-post-repost-${p.post_id}`}
            className="flex items-center gap-2 hover:text-primary transition-colors"
          >
            <Repeat2 className="w-5 h-5" /> {typeof p.share_count === "number" ? p.share_count : 0}
          </button>
        ) : (
          <span className="opacity-40 text-xs">—</span>
        )}
      </div>

      <Collapsible open={commentsOpen}>
        <CollapsibleContent className="mt-3" data-testid={`profile-post-comments-${p.post_id}`}>
          <CommentThread
            postId={p.post_id}
            comments={p.comments || []}
            onNewComment={onNewComment}
            onLocalUpdate={onCommentLocalUpdate}
            onDeleted={onCommentDeleted}
            onEdited={onCommentEdited}
          />
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}

function UserList({ users, emptyText, testidPrefix, onRemove }) {
  if (users.length === 0) return <Card className="p-6 text-center text-sm text-muted-foreground">{emptyText}</Card>;
  return (
    <div className="grid sm:grid-cols-2 gap-2">
      {users.map(u => (
        <Card key={u.user_id} className="p-3 flex items-center gap-3" data-testid={`${testidPrefix}-${u.user_id}`}>
          <Avatar className="w-10 h-10"><AvatarImage src={u.picture} /><AvatarFallback>{u.name?.[0]}</AvatarFallback></Avatar>
          <div className="flex-1 min-w-0">
            <Link to={`/user/${u.user_id}`} className="font-semibold text-sm hover:underline truncate block">{u.name}</Link>
            <div className="text-[10px] text-muted-foreground"><Users className="w-3 h-3 inline mr-1" /> {u.followers_count || 0} seguidores</div>
          </div>
          {onRemove ? (
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => onRemove(u)} data-testid={`remove-follower-${u.user_id}`}>Remover</Button>
          ) : <FollowButton userId={u.user_id} />}
        </Card>
      ))}
    </div>
  );
}
